/*
 * Pop-up de cupom.
 *
 * Aparece depois de alguns segundos, capta o e-mail pelo formulário de
 * cliente da Shopify e revela o código do cupom na própria tela.
 *
 * O envio é o normal do navegador: a loja usa a proteção antispam da
 * Shopify, e o token do captcha só é preenchido nesse caminho. A página
 * recarrega, e o cupom aparece na volta, pelo marcador que o Liquid deixa.
 */

// O formulário da Shopify traz campos ocultos (form_type, utf8) antes dos
// nossos. Sem excluir type="hidden", o primeiro "focável" seria um deles e o
// foco não sairia do lugar.
const FOCAVEIS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/* Campo escondido por CSS também não recebe foco: offsetParent entrega isso. */
const visiveis = (raiz) => [...raiz.querySelectorAll(FOCAVEIS)].filter((el) => el.offsetParent !== null);

class PopupCupom extends HTMLElement {
  connectedCallback() {
    this.caixa = this.querySelector('.popup-cupom__caixa');
    this.form = this.querySelector('form');
    this.erro = this.querySelector('[data-erro]');
    this.chave = this.dataset.chave;

    // Marcadores que o Liquid deixa quando a página volta de um envio.
    const cadastrou = this.querySelector('[data-cadastrou]');
    const erroServidor = this.querySelector('[data-erro-servidor]');

    this.querySelectorAll('[data-fechar]').forEach((b) => b.addEventListener('click', () => this.fechar()));
    this.addEventListener('keydown', (e) => this.aoTeclar(e));
    if (this.form) this.form.addEventListener('submit', () => this.enviar());

    const copiar = this.querySelector('[data-copiar]');
    if (copiar) copiar.addEventListener('click', () => this.copiar());

    // Quem acabou de se cadastrar vê o cupom na hora, sem esperar os segundos
    // e mesmo que o pop-up já estivesse calado: ele pediu para ver isso.
    if (cadastrou) {
      this.mostrarCupom();
      this.abrir();
      return;
    }

    if (this.estaCalado()) return;

    if (erroServidor) {
      this.abrir();
      this.mostrarErro(erroServidor.textContent.trim());
      return;
    }

    const atraso = Number(this.dataset.atraso || 5) * 1000;
    this.timerAbertura = setTimeout(() => this.abrir(), atraso);
  }

  disconnectedCallback() {
    clearTimeout(this.timerAbertura);
    clearInterval(this.relogio);
  }

  /* ---------- abrir e fechar ---------- */

  abrir() {
    this.hidden = false;
    document.body.classList.add('popup-cupom-travado');
    this.iniciarRelogio();

    // Tudo no quadro seguinte: enquanto o atributo hidden não sai do cálculo
    // de estilo, a caixa ainda é display:none e não aceita foco. É também o
    // intervalo de que a transição de entrada precisa para acontecer.
    requestAnimationFrame(() => {
      this.classList.add('popup-cupom--aberto');
      const etapa = this.querySelector('[data-etapa]:not([hidden])');
      if (etapa) visiveis(etapa)[0]?.focus({ preventScroll: true });
    });
  }

  fechar() {
    this.classList.remove('popup-cupom--aberto');
    document.body.classList.remove('popup-cupom-travado');
    clearInterval(this.relogio);
    setTimeout(() => { this.hidden = true; }, 200);   // deixa a transição terminar
    this.calar();
  }

  aoTeclar(e) {
    if (e.key === 'Escape') { this.fechar(); return; }
    if (e.key !== 'Tab') return;

    // Prende o Tab dentro do pop-up: quem está aqui não deve tabular para a
    // loja atrás, que continua na tela.
    const itens = visiveis(this.caixa);
    if (!itens.length) return;

    const primeiro = itens[0];
    const ultimo = itens[itens.length - 1];

    if (e.shiftKey && document.activeElement === primeiro) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primeiro.focus();
    }
  }

  /* ---------- contador ---------- */

  iniciarRelogio() {
    const alvo = this.querySelector('[data-relogio-numero]');
    let restante = Number(this.dataset.contador || 0);
    if (!alvo || restante <= 0) return;

    const pintar = () => {
      const min = Math.floor(restante / 60);
      const seg = String(restante % 60).padStart(2, '0');
      alvo.textContent = `${min}:${seg}`;
    };

    pintar();
    this.relogio = setInterval(() => {
      restante = Math.max(0, restante - 1);
      pintar();
      if (restante === 0) clearInterval(this.relogio);
    }, 1000);
  }

  /* ---------- cadastro ---------- */

  /*
   * O envio é o normal do navegador, de propósito: a loja usa a proteção
   * antispam da Shopify, e o token do captcha só é preenchido nesse caminho.
   * Por fetch o servidor devolve 400. Então a página recarrega, e quem mostra
   * o cupom é o marcador que o Liquid deixa na volta.
   */
  enviar() {
    const texto = this.form.querySelector('[data-botao-texto]');
    if (texto) texto.textContent = 'Enviando...';
    this.mostrarErro('');
  }

  /* Troca o convite pelo cupom. O foco quem dá é abrir(), no quadro seguinte. */
  mostrarCupom() {
    this.querySelector('[data-etapa="convite"]').hidden = true;
    this.querySelector('[data-etapa="cupom"]').hidden = false;
    this.dataset.contador = '0';   // sem relógio na tela do cupom
    this.calar();
  }

  mostrarErro(mensagem) {
    if (!this.erro) return;
    this.erro.textContent = mensagem;
    this.erro.hidden = !mensagem;
  }

  async copiar() {
    const codigo = this.querySelector('[data-codigo]')?.textContent.trim();
    const aviso = this.querySelector('[data-copiar-texto]');
    if (!codigo) return;

    try {
      await navigator.clipboard.writeText(codigo);
      if (aviso) {
        aviso.textContent = 'Copiado!';
        setTimeout(() => { aviso.textContent = 'Copiar'; }, 2000);
      }
    } catch {
      // Sem permissão de área de transferência o código continua na tela,
      // dá para selecionar na mão. Não vale travar nada por isso.
      if (aviso) aviso.textContent = 'Copie acima';
    }
  }

  /* ---------- silêncio entre visitas ---------- */

  estaCalado() {
    try {
      return Number(localStorage.getItem(this.chave) || 0) > Date.now();
    } catch {
      return false;   // aba anônima: mostra, e pronto
    }
  }

  calar() {
    const dias = Number(this.dataset.diasSilencio || 7);
    try {
      localStorage.setItem(this.chave, String(Date.now() + dias * 24 * 60 * 60 * 1000));
    } catch {
      /* sem armazenamento: o pop-up volta na próxima visita, sem quebrar nada */
    }
  }
}

if (!customElements.get('popup-cupom')) {
  customElements.define('popup-cupom', PopupCupom);
}
